import React, { useEffect, useState } from "react";
import { Steps, Button, Modal, Typography, message, Alert } from "antd";
import ItineraryStep from "./steps/ItineraryStep";
import VehicleStep from "./steps/VehicleStep";
import ContactStep from "./steps/ContactStep";
import ReviewStep from "./steps/ReviewStep";
import "./ExpressBooking.css";
import { ReactComponent as Route } from "../../assets/icons/Route.svg";
import { ReactComponent as Bus } from "../../assets/icons/Bus.svg";
import { ReactComponent as User } from "../../assets/icons/User_s.svg";
import { ReactComponent as Check } from "../../assets/icons/Check_c.svg";
import { ReactComponent as Left } from "../../assets/icons/Left_a.svg";
import { ReactComponent as Right } from "../../assets/icons/Right_a.svg";
import { ulid } from "ulid";
import { Space } from "antd";
import { Charter } from "../../model/charter";
import { BusRoute, DATE_STRING } from "../../model/busRoute";
import { createAcronym } from "../Charters/CharterItinerary";
import { charterModelData, customerModelData, routeModelData, routesModelExpiryService, shiftBatModelData, stopModelData } from "../../services/ModelService";
import { ShiftBat } from "../../model/shiftBat";
import useAllStops from "../../hooks/useAllStops";
import useAllTransfers from "../../hooks/useAllTransfers";
import useAllRoutes from "../../hooks/useAllRoutes";
import { deadrunModelData } from "../../services/ModelService";
import async from "async";
import { last } from "lodash";
import { Customer } from "../../model/customer";
import LoaderButton from "../../components/LoaderButton";
import useAllCustomers from "../Customers/useAllCustomers";
import useAllVehicleTypes from "../../hooks/useAllVehicleType";
import { calculateTotalShiftPayment } from "../../libs/eaPayments";
import { useAppContext } from "../../libs/contextLib";
import useAllSchedules from "../../hooks/useAllSchedules";
import dayjs from "../../dayjs";
import useAllCharters from "../Charters/useAllCharters";
import useAllCharterSchedules from "../../hooks/useAllCharterSchedules";
import { sendEmailViaTemplate } from "../../libs/mailer";
import { getKey } from "../../libs/hooksLib";
import { getPubOperator } from "../../services/routeService";
import { API } from "aws-amplify";
import { normaliseDate } from "../../libs/routes-lib";

const {Text, Title} = Typography

const { Step } = Steps;

const getInitialStopData = () => {

  return [ulid(), ulid()].map((id) => ({
    id: id,
    stopId: id,
    stopTimeId: id,
    stopName: "",
    stopAddress: "",
    stopLatitude: "",
    stopLongitude: "",
    stopType: "",
    stopSequence: "",
    location: "",
    coordinates: null,
    name: "",
    address: "",
    departDate: null,
    departTime: null,
    estimatedArrivalTime: null,
  }))
}

const INITIAL_CHARTER_DATA = {
  charterId: ulid(),
  name: "New Charter",
  status: "new",
  type: "express",
  payType: "charter",
  charterType: "express",
  numberOfPassengers: null,
  itinerary: {
    stops: getInitialStopData(),
    passengers: null,
    departDate: null,
    departTime: null,
  },
  vehicles: [],
  vehicleTypeQuantities: {},
  contact: {
    name: "",
    email: "",
    phone: "",
    company: "",
    specialRequests: "",
  },
}

// Create route and stops from itinerary data
export const createRouteFromItinerary = async (charterData) => {
  const routeName = `${charterData.itinerary.stops[0].name} to ${charterData.itinerary.stops[charterData.itinerary.stops.length - 1].name}`;
  const route = new BusRoute({
    routeId: ulid(),
    routeNumber: createAcronym(routeName),
    routeName: routeName,
    routeDetails: routeName,
    published: 1,
    charter: true,
    vehicleTypes: charterData?.vehicles?.map((vehicle) => vehicle.vehicleTypeId),
    vehicleTypeQuantities: charterData?.vehicleTypeQuantities,
    startDepot: charterData.itinerary.startDepot,
    endDepot: charterData.itinerary.endDepot,
  });
  const trip = route.addTrip();

  trip.vehicleTypes = route.vehicleTypes;
  trip.vehicleTypeQuantities = route.vehicleTypeQuantities;

  // Convert times to seconds since midnight
  const stops = [];
  const stopTimes = [];
  const firstStop = charterData.itinerary.stops[0];
  const lastStop = last(charterData.itinerary.stops);

  // Add all stops except the last one (destination)
  for (let i = 0; i < charterData.itinerary.stops.length; i++) {
    const stop = charterData.itinerary.stops[i];
    if (!stop.coordinates || stop.coordinates.length !== 2 || !stop.lon || !stop.lat) {
      console.error(`Stop ${i + 1} is missing valid coordinates`);
      continue;
    }

    const departTime = normaliseDate(stop.departTime || charterData.itinerary.departTime);
    const departTimeSecs = departTime ?
      departTime.hour() * 3600 +
      departTime.minute() * 60 : 0;

    const _stop = {
      stopId: stop.id,
      address: stop.address,
      stopName: stop.name,
      lon: stop.coordinates[0] || stop.lon,
      lat: stop.coordinates[1] || stop.lat,
      departSecs: departTimeSecs,
      verified: 1,
      date: stop.departDate ? [stop.departDate] : [],
    };

    const _stopTime = {
      stopId: _stop.stopId,
      stopTimeId: ulid(),
      arriveSecs: departTimeSecs,
      departSecs: departTimeSecs,
      timingPoint: true,
      date: stop.departDate ? [stop.departDate] : [],
    };

    stops.push(_stop);
    stopTimes.push(_stopTime);
  }

  if (stops.length === 0) {
    throw new Error('No valid stops with coordinates found');
  }

  // Add all stops in order to calculate the correct time and distance for each stop
  await async.series(stops.map((stop, i) => async () => {
    await route.addStop({...stop}, "append", true);
    route.calculateStartEnd({});
    if (stop.departSecs) {
      if (i === 0) {
        route.stopTimes[i].arriveSecs = stop.departSecs;
        route.stopTimes[i].departSecs = stop.departSecs;
        route.stops[i].arriveSecs = stop.departSecs;
        route.stops[i].departSecs = stop.departSecs;
        trip.setStartTime(stop.departSecs);
      }
      route.stopTimes[i].departSecs = stop.departSecs;
      route.stops[i].departSecs = stop.departSecs;
      last(trip.stopTimes).departSecs = stop.departSecs;
    }
  }));

  // Add return stop if enabled
  if (firstStop?.returnEnabled) {
    if (!firstStop.coordinates || firstStop.coordinates.length !== 2) {
      console.error('Return stop is missing valid coordinates');
    } else {
      stopTimes.push({
        stopId: firstStop.id,
        stopTimeId: ulid(),
        address: firstStop.address,
        stopName: firstStop.name,
        arriveSecs: 0,
        departSecs: 0,
      });
      await route.addStop({...stops[0], departSecs: 0, arriveSecs: 0}, "append", true);
    }
  }

  route.returnEnabled = firstStop?.returnEnabled;

  return { route, stops };
};

const validateItineraryStep = (itinerary) => {
  // Check if we have at least start and destination stops
  if (!itinerary.stops || itinerary.stops.length < 2) {
    return { valid: false, error: "At least start and destination stops are required" };
  }

  // Check if start and destination stops have location information
  const startStop = itinerary.stops[0];
  const destinationStop = itinerary.stops[itinerary.stops.length - 1];

  if (!startStop.coordinates || !startStop.location) {
    return { valid: false, error: "Starting point location is required" };
  }

  if (!destinationStop.coordinates || !destinationStop.location) {
    return { valid: false, error: "Destination location is required" };
  }

  // Check if start stop has pickup time
  if (!startStop.departTime) {
    return { valid: false, error: "Pickup time for starting point is required" };
  }

  // Check if destination stop has dropoff time only if return is enabled
  if (startStop.returnEnabled && !destinationStop.departTime) {
    return { valid: false, error: "Dropoff time for destination is required when return is enabled" };
  }

  // Check all intermediate stops for complete information
  for (let i = 1; i < itinerary.stops.length - 1; i++) {
    const stop = itinerary.stops[i];
    
    // If a stop has any information, it must have complete information
    if (stop.location || stop.coordinates || stop.departTime) {
      if (!stop.coordinates || !stop.location) {
        return { valid: false, error: `Stop ${i} location is required` };
      }
      
      if (!stop.departTime) {
        return { valid: false, error: `Pickup time for stop ${i} is required` };
      }
    }
  }

  // Check if passenger count is provided
  if (!itinerary.passengers || itinerary.passengers <= 0) {
    return { valid: false, error: "Number of passengers is required" };
  }

  // Remove any stops without location information (except start and destination)
  const validStops = [startStop];
  for (let i = 1; i < itinerary.stops.length - 1; i++) {
    const stop = itinerary.stops[i];
    if (stop.coordinates && stop.location) {
      validStops.push(stop);
    }
  }
  validStops.push(destinationStop);

  return { valid: true, cleanedStops: validStops };
};

const validateVehicleStep = (vehicles) => {
  if (!vehicles || vehicles.length === 0) {
    return { valid: false, error: "At least one vehicle must be selected" };
  }
  return { valid: true };
};

const validateContactStep = (contact) => {
  // If a customer is selected from existing customers
  if (contact.customerId) {
    return { valid: true };
  }

  // For new customers
  if (!contact.name || !contact.name.trim()) {
    return { valid: false, error: "Name is required" };
  }

  if (!contact.phone || !contact.phone.trim()) {
    return { valid: false, error: "Phone number is required" };
  }

  return { valid: true };
};

const ExpressBooking = ({ isModal = false, onClose, isOpen = false, isEmbedded = false, operatorId = null }) => {
  const {operator, messageApi} = useAppContext()
  const [currentStep, setCurrentStep] = useState(0);
  const [charterData, setCharterData] = useState(INITIAL_CHARTER_DATA);
  const [loading, setLoading] = useState(false);
  const [bookingCreated, setBookingCreated] = useState(false);
  const [validationError, setValidationError] = useState("");
  const {allCustomers} = useAllCustomers();
  const { allVehicleTypesArray, allVehicleTypes } = useAllVehicleTypes();
  const { allCharterSchedules } = useAllCharterSchedules();
  const { allChartersArray } = useAllCharters();

  // Determine if vehicle step should be shown
  const hasVehicleTypes = Array.isArray(allVehicleTypesArray) && allVehicleTypesArray.length > 0;

  useEffect(() => {
    async function onLoad() {
      const operatorKey = operator?.operatorId || await getKey();
        await routesModelExpiryService.init({
          apiKey: operatorKey,
          modelFilter: ['Schedule', 'Stop', 'VehicleType', 'Customer', 'Charter']
        })
        routesModelExpiryService.setOperator(operator);
    }

    onLoad().then(() => {
      console.log('routesModelExpiryService initialized');
    });

    return () => {
      routesModelExpiryService.deinit();
    }
  }, [operatorId, operator?.operatorId]);

  const reset = () => {
    setCharterData(INITIAL_CHARTER_DATA);
    setCurrentStep(0);
    setBookingCreated(false);
    setValidationError("");
  }

  const handleClose = () => {
    reset();
    if (onClose) onClose();
  }

  // Dynamically build steps based on vehicle types
  const steps = [
    {
      title: "Itinerary",
      icon: <Route />,
      content: <ItineraryStep data={charterData.itinerary} onChange={(data) => setCharterData((prev) => ({ ...prev, itinerary: data }))} isEmbedded={isEmbedded} />, 
    },
    ...(hasVehicleTypes ? [{
      title: "Vehicle",
      icon: <Bus style={{ width: 24 }} />, 
      content: <VehicleStep 
        data={charterData.vehicles} 
        onChange={(data) => {
          const vehicles = data;
          const vehicleTypeQuantities = data.reduce((acc, vehicle) => {
            acc[vehicle.vehicleTypeId] = vehicle.quantity || 1;
            return acc;
          }, {});
          setCharterData((prev) => ({ 
            ...prev, 
            vehicles,
            vehicleTypeQuantities 
          }));
        }} 
      />,
    }] : []),
    {
      title: "Contact",
      icon: <User />,
      content: <ContactStep data={charterData.contact} onChange={(data) => setCharterData((prev) => ({ ...prev, contact: data }))} isEmbedded={isEmbedded} />, 
    },
    {
      title: "Review",
      icon: <Check />,
      content: <ReviewStep data={charterData} />, 
    },
  ];

  const handleNext = () => {
    let validationResult;
    setValidationError("");

    // Find which step we're on by title
    const stepTitle = steps[currentStep]?.title;
    switch (stepTitle) {
      case "Itinerary":
        validationResult = validateItineraryStep(charterData.itinerary);
        if (!validationResult.valid) {
          setValidationError(validationResult.error);
          return;
        }
        // Update the stops with cleaned data (removing invalid intermediate stops)
        if (validationResult.cleanedStops) {
          setCharterData(prev => ({
            ...prev,
            itinerary: {
              ...prev.itinerary,
              stops: validationResult.cleanedStops
            }
          }));
        }
        break;
      case "Vehicle":
        if (hasVehicleTypes) {
          validationResult = validateVehicleStep(charterData.vehicles);
          if (!validationResult.valid) {
            setValidationError(validationResult.error);
            return;
          }
        }
        break;
      case "Contact":
        validationResult = validateContactStep(charterData.contact);
        if (!validationResult.valid) {
          setValidationError(validationResult.error);
          return;
        }
        break;
      default:
        break;
    }

    setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    try {
      console.log("Creating Express Booking")
      setLoading(true);

      const bookingReference = await charterModelData.generateBookingReference();
      console.log("Booking Reference", bookingReference)
      charterData.bookingReference = bookingReference;

      await createBookingEnquiry();
      await sendInquiryConfirmationEmail(bookingReference);

      if (isEmbedded) {
        console.log("Booking created", charterData)
        setBookingCreated(true);
      } else {
        console.log("Booking created", charterData)
        handleClose();
      }
    } catch (error) {
      console.error("Failed to create charter:", error);
      messageApi.error("Failed to create charter");
    } finally {
      setLoading(false);
    }
  };

  const sendInquiryConfirmationEmail = async (bookingReference) => {
    const operatorKey = operatorId || await getKey();
    const operator = await getPubOperator(operatorKey);
    await sendEmailViaTemplate({
      to: [charterData.contact.email],
      template: `${operator?.opts?.email?.templatePrefix || 'busable'}-Inquiry: Charter`,
      merge: {
        customerFirstName: charterData.contact.name || 'Customer',
        pickupLocation: charterData.itinerary?.stops?.[0]?.stopName || 'TBC',
        dropOffLocation: charterData.itinerary?.stops?.[charterData.itinerary.stops.length - 1]?.stopName || 'TBC',
        travelDates: charterData.dates?.map(date => dayjs(date).format('DD/MM/YYYY')).join(', '),
        bookingReference: bookingReference,
        noOfPassengers: charterData.itinerary.passengers?.toString() || 'TBC',
        companyName: operator?.accreditationName || "",
        companyPhone: operator?.operatorPhone,
        companyEmail: operator?.operatorEmail,
        companyWebsite: operator?.operatorUrl,
      },
      subject: `Your Charter Inquiry has been received`,
    });
  }

  const createBookingEnquiry = async () => {
    try {
      const key = operatorId || operator?.operatorId || await getKey();
      console.log("Creating booking enquiry", charterData)
      const body = {
        ...charterData,
        itineraryData: charterData.itinerary,
      }
      delete body.itinerary;
      const response = await API.post('charter', `/booking/enquiry?_k=${key}`, {
        body
      });
      console.log("Booking Enquiry created", response)
    } catch (error) {
      console.error("Failed to create booking enquiry:", error);
      messageApi.error("Failed to create booking enquiry");
      throw error;
    }
  }

  const content = (
    <div className="BookingForm">
      {bookingCreated ? (
        <div className="booking-success-message">
          <Check style={{ width: 64, height: 64, marginBottom: 24 }} />
          <Title level={3} onClick={() => console.log('charterData', charterData)}>Thank you, your booking has been created</Title>
          <Text>Booking Reference: {charterData.bookingReference}</Text>
          <Button type="primary" size="large" className="btn-primary" onClick={() => navigator.clipboard.writeText(charterData.bookingReference)} style={{ marginTop: 24 }}>
            Copy Booking Reference
          </Button>
          <Text>We'll be in touch with you shortly to confirm the details.</Text>
          <Button type="primary" size="large" className="btn-primary" onClick={handleClose} style={{ marginTop: 24 }}>
            Close
          </Button>
        </div>
      ) : (
        <>
          <div className="steps-navigation">
            <Button type="text" icon={<Left />} disabled={currentStep === 0} onClick={handlePrev} className="nav-button prev" />
            <Steps current={currentStep} responsive={false}>
              {steps.map((item) => (
                <Step key={item.title} title={item.title} icon={item.icon} />
              ))}
            </Steps>
            <Button type="text" icon={<Right />} disabled={currentStep === steps.length - 1} onClick={handleNext} className="nav-button next" />
          </div>
          <div className="steps-content">{steps[currentStep].content}</div>
          <div className="steps-action justify-content-end">
            {validationError && (
              <Alert message={validationError} type="error" />
            )}
            {currentStep === steps.length - 1 ? (
              <LoaderButton 
                type="primary" 
                size="large" 
                className="btn-primary" 
                onClick={handleSubmit}
                isLoading={loading}
              >
                Create Booking
              </LoaderButton>
            ) : (
              <Button type="primary" size="large" className="btn-primary" onClick={handleNext}>Next</Button>
            )}
          </div>
        </>
      )}
    </div>
  );

  if (isModal) {
    return (
      <Modal
        title="Create Express Booking"
        open={isOpen}
        onCancel={handleClose}
        onClose={handleClose}
        width={800}
        footer={null}
        centered
        destroyOnClose={true}
      >
        {content}
      </Modal>
    );
  }

  return content;
};

export default ExpressBooking;
